import React from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import SectionMessage from '@atlaskit/section-message/message';

import { i18n } from '../../widgetMacroI18n.ts';
import type { EmbedDescriptor } from '../../widgetMacroTypes.ts';

type Props = {
	descriptor: EmbedDescriptor;
};

/**
 * Renders a contextual deprecation notice for providers that are no longer
 * supported. The `hostLabel` on the descriptor identifies the sub-case:
 *
 * - `'myspace-outdated'` – legacy `vids.myspace.com` URL; shows the MySpace
 *                          outdated message.
 * - `'flash-deprecated'` (default) – provider relied on Flash (DailyMotion,
 *                          Flickr, Tudou); shows the Flash deprecation message.
 */
export const DeprecatedEmbed = ({ descriptor }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	if (descriptor.hostLabel === 'myspace-outdated') {
		return (
			<Box testId="widget-macro-deprecated-myspace">
				<SectionMessage appearance="warning">{formatMessage(i18n.myspaceOutdated)}</SectionMessage>
			</Box>
		);
	}

	// Default: flash-deprecated providers (DailyMotion, Flickr, Tudou)
	return (
		<Box testId="widget-macro-deprecated">
			<SectionMessage appearance="warning">{formatMessage(i18n.flashDeprecated)}</SectionMessage>
		</Box>
	);
};
